import { browserBlock, runSnapshot, versionBlock } from "@/systems/snapshot";
import type { GameState } from "@/state/GameState";

/**
 * The Grievance Box payload + send path (v0.12 RFC §3.2). The builder is
 * PURE (headless-tested: clamp boundary, opt-in semantics, field-named
 * rejections); the send wrapper owns the endpoint/dev gating.
 *
 * Fail-loud where it's a caller bug (empty/oversize text), graceful where
 * it's the world's fault (oversize save → resend without it; the text
 * ALWAYS survives — RFC G3).
 */

export type FeedbackCategory = "bug" | "idea";

/** switchboard's global MAX_BODY_BYTES (bumped for us 2026-07-20). */
export const FEEDBACK_BODY_CAP = 65536;
export const FEEDBACK_TEXT_MAX = 4000;

export interface FeedbackBody {
  body: string;
  /** Whether the save made it into `body` (false = not offered, or clamped out). */
  saveIncluded: boolean;
  /** True when the save was OFFERED but clamped out by the 64KB cap. */
  saveTooLarge: boolean;
}

export function buildFeedbackBody(
  state: GameState,
  category: FeedbackCategory,
  text: string,
  attachSave: boolean,
): FeedbackBody {
  const trimmed = text.trim();
  if (trimmed.length === 0) {
    throw new Error("buildFeedbackBody: text must be non-empty");
  }
  if (trimmed.length > FEEDBACK_TEXT_MAX) {
    throw new Error(`buildFeedbackBody: text must be <= ${FEEDBACK_TEXT_MAX} chars (got ${trimmed.length})`);
  }
  // switchboard's contract (live-smoked TWICE at review): `message` is
  // the ONLY field — ANY sibling gets a 400 "invalid json" (strict
  // schema, misleadingly named error). So the body is {message} alone
  // and the message must be COMPLETE: note + context line + consented
  // save. Silver lining: no double-carry, the full attach cap is back.
  const v = versionBlock();
  const contextLine = `[halfway v${v.version} L${v.logic} S${v.schema} · ${category} · ${JSON.stringify(browserBlock())} · ${JSON.stringify(runSnapshot(state))}]`;
  const compose = (save: { account: unknown; run: unknown } | null, saveNote: string): string =>
    JSON.stringify({
      message: `${trimmed}\n\n${contextLine}${save !== null ? `\n\n[save]\n${JSON.stringify(save)}` : saveNote}`,
    });
  if (attachSave) {
    const withSave = compose({ account: state.account, run: state.run }, "");
    if (byteLength(withSave) <= FEEDBACK_BODY_CAP) {
      return { body: withSave, saveIncluded: true, saveTooLarge: false };
    }
    // Clamp: the report still lands, marked — the text always survives.
    return {
      body: compose(null, "\n\n[save: TOO LARGE TO ATTACH]"),
      saveIncluded: false,
      saveTooLarge: true,
    };
  }
  return { body: compose(null, ""), saveIncluded: false, saveTooLarge: false };
}

function byteLength(s: string): number {
  return new TextEncoder().encode(s).length;
}

export type SendResult = { sent: true; saveTooLarge: boolean } | { sent: false; dev: true };

/**
 * POST a grievance to switchboard. Dev builds carry no endpoint constant
 * — the payload logs LOUDLY and nothing leaves the machine (never spam
 * the real inbox from dev/e2e). Network/HTTP failure REJECTS so the
 * sheet can show its honest failure state with the text kept.
 */
export async function sendFeedback(
  state: GameState,
  category: FeedbackCategory,
  text: string,
  attachSave: boolean,
): Promise<SendResult> {
  const url = import.meta.env.VITE_FEEDBACK_URL as string | undefined;
  const built = buildFeedbackBody(state, category, text, attachSave);
  // PROD gate (v0.12 review, codex): env alone is NOT enough — a dev/e2e
  // build inheriting a .env must never post to the real inbox. And the
  // dev log carries the FULL body: 400 chars of a long report is not
  // "logged", it's lost (RFC G3 applies to dev testers too).
  if (!import.meta.env.PROD) {
    console.warn("[feedback] dev build — grievance NOT sent. Full payload:\n", built.body);
    return { sent: false, dev: true };
  }
  if (url === undefined || url === "") {
    // A PRODUCTION build without the endpoint is a broken release, not a
    // dev no-op — fail loud so the sheet shows its honest error and the
    // player's text survives (bot leg, codex).
    throw new Error("sendFeedback: VITE_FEEDBACK_URL is missing in a production build — release env misconfigured");
  }
  const res = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: built.body,
    // A stalled connection would pin the sheet's in-flight state and the
    // SEND button indefinitely (bot leg, codex) — bound it.
    signal: AbortSignal.timeout(15_000),
  });
  if (!res.ok) {
    throw new Error(`sendFeedback: switchboard answered ${res.status}`);
  }
  return { sent: true, saveTooLarge: built.saveTooLarge };
}
